import { ReactNode, useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PersistentStateContext } from 'context/PersistentState';
import { Instance as InstanceModel, Organisation, UserContext } from 'context/User';
import { TrackingContext } from 'context/Tracking';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { SignInErrorCodes } from 'pages/SignIn/errorCodes';
import { signInRoutes } from 'pages/SignIn/routes';
import { CustomProductRedirects } from 'pages/SignUp/CustomSignup.data';
import { signUpRoutes } from 'pages/SignUp/routes';
import { CustomSignupProductType, SignupStackRedirectOptions } from 'pages/SignUp/types';
import { CloudOrgData, SubscriptionCodeType } from 'types/orgTypes';
import { Endpoints, get } from 'utils/api';
import { GRAFANA_NET_DOMAIN, ORIGIN, PUBLIC_URL } from 'utils/consts';
import { getConfigProp } from 'utils/cookieConfig';
import { resolveInstanceUrl } from 'utils/resolveInstanceUrl';
import { isAbsoluteUrl } from 'utils/isAbsoluteUrl';
import { isInstanceUrl } from 'utils/isInstanceUrl';
import { useHistory, useLocationState } from 'utils/router';
import { getStackInstanceUrl } from 'utils/signupStack';

import { defaultRedirectContextState, RedirectContext } from './redirectContext';
import {
  ClearInstanceFn,
  GetRedirectUrlFn,
  GetSignupRedirectUrlFn,
  RedirectToComputedInstanceHomeFn,
  RedirectToComputedInstanceK6Fn,
  RedirectToComputedInstanceOnboardingFn,
  RedirectToCustomLoginRedirectFn,
  RedirectToPortalFn,
  RedirectToPortalOrInstanceHomeFn,
  RedirectToStateInstanceHomeFn,
  RedirectToStateInstanceOnboardingFn,
  RedirectToToFn,
  SetOrgIdFn,
  UpdateCustomContentFn,
} from './types';

const cloudHomePath = '/a/grafana-setupguide-app';

const instancePaths = {
  home: '/',
  onboardingFlow: `${cloudHomePath}/onboarding-flow`,
  connections: '/connections/add-new-connection',
  k6: '/a/k6-app',
};

export interface RedirectProviderProps {
  children: ReactNode;
}

export const RedirectProvider = ({ children }: RedirectProviderProps) => {
  const [redirectState, setRedirectState] = useState(defaultRedirectContextState);
  const { search } = useLocation();
  const state = useLocationState<{ redirectStack?: string }>();
  const history = useHistory();
  const [{ userData }, { signOut }] = useContext(UserContext);
  const [persistentState] = useContext(PersistentStateContext);
  const [, { getSourcesAsQueryParams }] = useContext(TrackingContext);
  const params = new URLSearchParams(search);

  useEffectOnce(() => {
    const toParam = params.get('to');
    const redirectPath = params.get('redirectPath');
    let to = redirectState.to;

    if (toParam) {
      if (!isAbsoluteUrl(toParam)) {
        to = !toParam.startsWith('/') ? `/${toParam}` : toParam;
      }
      params.delete('to');
    }

    let integration = params.get('integration');
    if (integration) {
      params.delete('integration');
    }

    let instance: string | null = null;
    const referrer = document.referrer;
    if (isInstanceUrl(referrer)) {
      instance = referrer;
    } else {
      const instanceParam = params.get('instance');
      if (isInstanceUrl(instanceParam)) {
        instance = instanceParam;
      }
      params.delete('instance');
    }

    history.replace({
      search: params.toString(),
    });

    setRedirectState((redirectState) => ({
      ...redirectState,
      initialized: true,
      instance,
      integration,
      to,
      redirectPath,
      customContent: persistentState?.customContent,
    }));
  });

  // START: STATE SETTERS
  const updateCustomContent: UpdateCustomContentFn = useCallback(
    (customContent?: CustomSignupProductType) =>
      setRedirectState((redirectState) => ({
        ...redirectState,
        redirectPath: customContent?.key ?? null,
        customContent,
      })),
    []
  );

  const setOrgId: SetOrgIdFn = (orgId = null) =>
    setRedirectState({
      ...redirectState,
      orgId,
    });

  const clearInstance: ClearInstanceFn = () => {
    setRedirectState((redirectState) => ({
      ...redirectState,
      instance: null,
    }));
  };
  // END: STATE SETTERS

  // START: GETTERS/SELECTORS
  const selectOrgFromList = (user = userData): Organisation => {
    let organisation: Organisation | undefined;

    const memberships = user?.memberships ?? [];
    const { orgId } = redirectState;

    // Try to get the org saved in the context
    if (orgId) {
      organisation = memberships.find((organisation) => organisation.orgId === orgId);
    }

    // Fallback to org saved in config
    if (!organisation) {
      const slugFromConfig = getConfigProp('org');

      if (slugFromConfig) {
        organisation = memberships.find((organisation) => organisation.orgSlug === slugFromConfig);
      }
    }

    // Fallback to the default org
    if (!organisation) {
      organisation = memberships.find((organisation) => organisation.defaultOrg);
    }

    // Fallback to first org in the list
    if (!organisation) {
      organisation = memberships[0];
    }

    return organisation;
  };

  const selectInstanceFromList = (instances: InstanceModel[], user = userData, stackId?: string) => {
    if (stackId) {
      const stack = instances.find((instance) => instance.id === Number(stackId));

      if (stack) {
        return stack;
      }
    }
    const orgId = redirectState.orgId ?? selectOrgFromList(user)?.orgId;
    console.log('selectInstanceFromList - instances', instances);
    return instances.find((instance) => instance.orgId === orgId) ?? instances[0];
  };

  const getInstances = async (user = userData) => {
    const memberships = user?.memberships ?? [];

    try {
      // Build the orgId list
      const orgIdIn = redirectState.orgId ?? memberships.map((organisation) => organisation.orgId).join(',');
      // Fetch the instances for the built orgId
      const instances = await get<{ items: InstanceModel[] }>(`${Endpoints.GET_INSTANCES}?orgIdIn=${orgIdIn}`);

      return instances?.items ?? [];
    } catch {
      return [];
    }
  };

  const getRedirectUrl: GetRedirectUrlFn = (path) => {
    const { instance, to } = redirectState;
    const queryParams = new URLSearchParams(getSourcesAsQueryParams());

    if (instance) {
      queryParams.set('instance', instance);
    }

    if (to) {
      queryParams.set('to', to);
    }

    let stringifiedQueryParams = queryParams.toString();

    if (stringifiedQueryParams) {
      stringifiedQueryParams = `?${stringifiedQueryParams}`;
    }

    return `${PUBLIC_URL}${path}${stringifiedQueryParams}`;
  };

  // Where the sign-up confirmation should land. skipOrgCreation variants (e.g. marketplace) go
  // straight to their portal flow, which provisions the org itself — bypassing SelectOrganisation so
  // no trial org/stack is created. Baked into the confirmation email so it also holds cross-browser.
  const getSignupRedirectUrl: GetSignupRedirectUrlFn = () => {
    const customContent = redirectState.customContent ?? persistentState?.customContent;

    if (customContent?.skipOrgCreation && customContent.redirect) {
      return `/${customContent.redirect}`;
    }

    return getRedirectUrl(signUpRoutes.selectOrganisation);
  };
  // END: GETTERS/SELECTORS

  // START: MAIN REDIRECT FUNCTIONS
  const redirectToPortal: RedirectToPortalFn = (user = userData) => {
    const organisation = selectOrgFromList(user);
    const customContent = redirectState.customContent ?? persistentState?.customContent;

    if (organisation) {
      window.location.href = `${ORIGIN}/orgs/${organisation.orgSlug}`;
    } else if (!customContent?.skipOrgCreation) {
      window.location.href = getRedirectUrl(signUpRoutes.selectOrganisation);
    } else {
      // skipOrgCreation variants create their own org, so select-org shows them no form.
      window.location.href = `${ORIGIN}/users/${user?.username}/settings`;
    }
  };

  const redirectToInstance = (
    path: string,
    instanceUrl: string,
    isActiveInstance?: boolean,
    loadingPageState?: { orgData?: CloudOrgData; stackReady?: boolean }
  ) => {
    // If the product should redirect to the portal, not the instance, bail out early.
    const portalRedirectContent = persistentState?.customContent?.isPortalRedirect
      ? persistentState.customContent
      : null;

    if (portalRedirectContent?.redirect) {
      window.location.href = `${ORIGIN}/${portalRedirectContent.redirect}`;
      return;
    }

    const actualPath = persistentState?.customContent?.redirect
      ? `/${persistentState.customContent?.redirect}`
      : path
        ? path
        : instancePaths.onboardingFlow;

    // if the grafana instance is active, skip the loading screen
    if (isActiveInstance) {
      console.log('redirectToInstance - isActiveInstance logged', isActiveInstance);
      window.location.href = resolveInstanceUrl(
        instanceUrl,
        `/set-redirect-and-login?url=${encodeURIComponent(actualPath)}`
      );
    } else {
      history.push(
        {
          pathname: signUpRoutes.loadingInstance,
          search: `?url=${encodeURIComponent(actualPath)}&instanceUrl=${encodeURIComponent(instanceUrl)}`,
        },
        loadingPageState
      );
    }
  };
  // END: MAIN REDIRECT FUNCTIONS

  // START: MAIN HANDLER FUNCTIONS THAT SPECIFY WHICH REDIRECT PATH TO USE
  // All of these functions will invoke either `redirectToInstance` or `redirectToPortal`
  // (found above)
  const redirectToStateInstance = async (path: string) => {
    const { instance } = redirectState;

    const instanceUrl = new URL(instance!);
    try {
      await get<{ items: InstanceModel[] }>(
        `${Endpoints.GET_INSTANCE}/${instanceUrl.host.replace('.' + GRAFANA_NET_DOMAIN, '')}`
      );
      redirectToInstance(path, instance!);
    } catch {
      signOut(signInRoutes.base, {
        errorCode: SignInErrorCodes.NO_ORG_ACCESS,
      });
    }
  };

  const redirectToStateInstanceHome: RedirectToStateInstanceHomeFn = async () =>
    redirectToStateInstance(instancePaths.home);

  const redirectToStateInstanceOnboarding: RedirectToStateInstanceOnboardingFn = async () =>
    redirectToStateInstance(instancePaths.connections);

  const redirectToComputedInstanceFromTrialStart = async (
    path: string,
    { trialStart, stackReadyFromStart }: SignupStackRedirectOptions
  ) => {
    const instanceUrl = getStackInstanceUrl(trialStart.stack);

    if (stackReadyFromStart) {
      redirectToInstance(path, instanceUrl, true);
      return;
    }

    let orgData: CloudOrgData | undefined;

    try {
      orgData = await get<CloudOrgData>(`${Endpoints.GET_ORGS}/${trialStart.org.id}`);
    } catch (err) {
      console.log('err', (err as Error).message);
    }

    redirectToInstance(path, instanceUrl, false, orgData ? { orgData } : undefined);
  };

  const redirectToComputedInstance = async (path: string, user = userData) => {
    const { orgId } = redirectState;
    const instances = await getInstances(user);

    if (instances.length === 0 || (instances.length > 1 && !state?.redirectStack)) {
      redirectToPortal(user);

      return;
    }

    const instance = selectInstanceFromList(instances, user, state?.redirectStack);
    let isActiveInstanceAndNotTrial = false;

    try {
      const tempOrgId = orgId ?? instance?.orgId;

      if (tempOrgId) {
        const orgData = await get<CloudOrgData>(`${Endpoints.GET_ORGS}/${tempOrgId}`);
        const currentProductIsFree: boolean = orgData?.subscriptions?.current.product === SubscriptionCodeType.FREE;
        const isInTrial = orgData?.subscriptions?.current.isTrial && currentProductIsFree;

        // We do not want new users to skip loading screen (who also have an instance status of 'active')
        isActiveInstanceAndNotTrial = instance?.status === 'active' && !isInTrial;
      }
    } catch (err) {
      console.log('err', (err as Error).message);
    }

    redirectToInstance(path, instance.url, isActiveInstanceAndNotTrial);
  };

  const redirectToComputedInstanceHome: RedirectToComputedInstanceHomeFn = async (user) =>
    redirectToComputedInstance(instancePaths.home, user);

  const redirectToComputedInstanceK6: RedirectToComputedInstanceK6Fn = async (user) =>
    redirectToComputedInstance(instancePaths.k6, user);

  const redirectToComputedInstanceOnboarding: RedirectToComputedInstanceOnboardingFn = async (user, signupStack) => {
    const params = new URLSearchParams(search);

    if (persistentState?.customContent) {
      params.set('redirectPath', persistentState?.customContent?.key as CustomProductRedirects);
    }

    // If the custom signup product should redirect to the portal (grafana.com)
    // instead of the Grafana instance, do so directly.
    if (persistentState.customContent?.isPortalRedirect && persistentState.customContent?.redirect) {
      window.location.href = `${ORIGIN}/${persistentState.customContent.redirect}`;
      return;
    }

    let path = instancePaths.onboardingFlow;
    if (persistentState.customContent?.redirect) {
      path = persistentState.customContent.redirect;
    }

    if (signupStack) {
      return redirectToComputedInstanceFromTrialStart(path, signupStack);
    }

    return redirectToComputedInstance(path, user);
  };

  const redirectToTo: RedirectToToFn = () => {
    const { to } = redirectState;

    if (to) {
      window.location.href = `${ORIGIN}${to}`;
    }
  };

  const redirectToCustomLoginRedirect: RedirectToCustomLoginRedirectFn = () => {
    const { customContent } = redirectState;
    // Fall back to persistentState in case redirectState hasn't been initialized yet
    // (e.g., PostSSO/SelectOrganisation effects fire before RedirectProvider's useEffectOnce).
    const loginRedirect = customContent?.loginRedirect ?? persistentState?.customContent?.loginRedirect;

    if (loginRedirect) {
      // Currently all loginRedirect products use portal redirects.
      // Instance-based login redirects would need separate handling if added.
      window.location.href = `${ORIGIN}/${loginRedirect}`;
    } else {
      redirectToPortal();
    }
  };

  const redirectToPortalOrInstanceHome: RedirectToPortalOrInstanceHomeFn = async (user) => {
    const actualUser = user ?? userData;
    const orgsNo = actualUser?.memberships.length ?? 0;

    if (orgsNo > 1) {
      redirectToPortal(user);
    } else {
      const instances = await getInstances(actualUser);

      if (instances.length === 1) {
        redirectToInstance(instancePaths.home, instances[0].url);
      } else {
        redirectToPortal(actualUser);
      }
    }
  };
  // END: MAIN HANDLER FUNCTIONS THAT SPECIFIY WHICH REDIRECT PATH TO USE

  return (
    <RedirectContext.Provider
      value={[
        redirectState,
        {
          clearInstance,
          getRedirectUrl,
          getSignupRedirectUrl,
          redirectToComputedInstanceHome,
          redirectToComputedInstanceK6,
          redirectToComputedInstanceOnboarding,
          redirectToStateInstanceHome,
          redirectToStateInstanceOnboarding,
          redirectToPortal,
          redirectToTo,
          redirectToCustomLoginRedirect,
          redirectToPortalOrInstanceHome,
          setOrgId,
          updateCustomContent,
        },
      ]}
    >
      {redirectState.initialized ? children : null}
    </RedirectContext.Provider>
  );
};
